import { Pin, PinOff } from '@tunasong/icons'
import type { FC } from 'react'
import { ListItemIcon, ListItemText, MenuItem, type MenuItemProps } from '../primitives/index.js'
import { useAlert } from '../alert/alert.hook.js'
import type { FavoriteControlProps } from './favorite-props.js'
import { useFavorites } from './favorites.hook.js'

type PinMenuItemProps = FavoriteControlProps<MenuItemProps>

/**
 * Dashboard Phase 2 — entity-menu affordance that toggles pin state.
 *
 * Cap-handling pivot: the menu item is *always* enabled and labeled
 * "Pin to dashboard" / "Unpin from dashboard". Clicking while capped fires
 * an info toast instead of being silently disabled — disabled items feel
 * broken from the keyboard and obscure the affordance for screen-reader
 * users. The toast tells the user exactly what's happening and what to do
 * next (unpin one first).
 */
export const PinMenuItem: FC<PinMenuItemProps> = props => {
  const { onClick, entity, ...restProps } = props
  const { isPinned, pin, unpin, canPinMore, pinCap } = useFavorites()
  const { alert } = useAlert()

  const pinned = isPinned(entity.id)

  const label = pinned ? 'Unpin from dashboard' : 'Pin to dashboard'
  const Icon = pinned ? PinOff : Pin

  return (
    <MenuItem
      onClick={ev => {
        ev.preventDefault()
        ev.stopPropagation()
        if (pinned) {
          unpin(entity.id)
        } else if (!canPinMore) {
          alert({
            severity: 'info',
            message: `You can pin up to ${pinCap} items. Unpin one to make room.`,
          })
        } else {
          pin(entity)
        }
        if (onClick) {
          onClick(ev)
        }
      }}
      {...restProps}
    >
      <ListItemIcon>
        <Icon color={pinned ? 'var(--tuna-palette-primary-main)' : 'currentColor'} />
      </ListItemIcon>
      <ListItemText primary={label} />
    </MenuItem>
  )
}

export default PinMenuItem
